import { defineStore } from 'pinia';
import { getSiteList, getSiteOptions } from '@/api/common';
import { useAppStore } from '@/store';

interface SiteState {
  /** 全量站点列表（启动加载并持久化） */
  list: API.Site[]
  /** 站点分组选项（按需加载 + 无感刷新） */
  options: API.SiteOptionGroup[]
  /** 选项缓存时间戳，便于 SWR 判断 */
  optionsLoadedAt: number
  code: string
}

export const useSiteStore = defineStore({
  id: 'site',
  state: (): SiteState => ({
    list: [],
    options: [],
    optionsLoadedAt: 0,
    code: '',
  }),
  getters: {
    /** 获取当前站点 */
    currentSite(): API.Site | null {
      if (!this.code || !this.list.length) return null;
      if (useAppStore().independentSite) {
        return this.list.find(item => item.siteCode === useAppStore().independentSite);
      }
      return this.list.find(item => item.siteCode === this.code) || null;
    },
    /** 按站点编码查找站点 */
    findSiteByCode() {
      return (siteCode: string): API.Site | undefined => {
        return this.list.find(item => item.siteCode === siteCode);
      };
    },
    /** 当前是否为主站/代理站模式（非独立站） */
    isMultiSite(): boolean {
      return this.list.length > 1;
    },
    /** 当前是否支持显示礼品 */
    allowGif(): boolean {
      return this.list.findIndex(item => item.isGiftOrder > 0) !== -1;
    },
  },
  actions: {
    /**
     * 获取全量站点列表（新接口）
     * 仅在启动 / 显式刷新时调用，结果合并写入 this.list 并持久化
     */
    async fetchSiteList(independentSite?: string) {
      const params: Record<string, any> = {};
      const res = await getSiteList(params, independentSite);
      // 过滤掉关闭站点（0） 和 异常站点（-1）等
      // 站点独立站的时候不进行过滤（适配未正式上线的站点 如：阳美站）
      const validList = (res || []).filter(item => independentSite || (item && item.status > 0));
      this.list = this.mergeSiteList(validList);
      if (independentSite && independentSite !== this.code) {
        this.code = independentSite;
      }
      if (!this.code) {
        this.code = this.list[0].siteCode || '';
      }
    },

    /**
     * 获取站点分组选项（按需调用，stale-while-revalidate）
     * - 已有缓存且非 force：立即返回缓存，后台刷新写入 options
     * - 无缓存：直接 await 请求并写入
     */
    async fetchSiteOptions(independentSite?: string, force = false): Promise<API.SiteOptionGroup[]> {
      const params: Record<string, any> = {};

      const requestAndStore = async () => {
        const res = await getSiteOptions(params, independentSite);
        const list = (res || []).filter(item => item && item.siteList && item.siteList.length > 0);
        this.options = list;
        this.optionsLoadedAt = Date.now();
        return list;
      };

      if (this.options.length && !force) {
        // 后台无感刷新，错误静默吞掉，不影响 UI
        requestAndStore().catch(() => { /* silent */ });
        return this.options;
      }

      return requestAndStore();
    },

    /* 合并列表，更新本地站点列表 */
    mergeSiteList(newList: API.Site[]) {
      if (!this.list || !this.list.length) return newList;

      // 创建一个Map以便快速查找新站点列表，
      const serverMap = new Map(newList.map(item => [item.id, item]));

      const resultList: API.Site[] = [];
      // 修改已存在的数据
      this.list.forEach((localItem) => {
        const serverItem = serverMap.get(localItem.id);
        if (serverItem) {
          // 获取到站点，更新站点数据
          resultList.push({
            ...localItem,
            ...serverItem,
          });
          // 从Map中删除
          serverMap.delete(localItem.id);
        }
      });
      // 遍历剩下的站点，为新增站点
      serverMap.forEach((item) => {
        resultList.push(item);
      });

      return resultList;
    },

    /** 切换当前站点 */
    changeCurrentSite(site: API.Site) {
      const code = site.siteCode || '';
      if (code && code !== this.code) {
        this.code = code;
      }
    },

    /** 按指定站点ID顺序重排列表 */
    reorderSiteList(siteIds: number[]) {
      const idSet = new Set(siteIds);
      // 保留未在 siteIds 中的站点（保持原顺序追加在后面）
      const remaining = this.list.filter(s => !idSet.has(s.siteId || s.id));
      const reordered = siteIds
        .map(id => this.list.find(s => (s.siteId || s.id) === id))
        .filter(Boolean) as API.Site[];
      this.list = [...reordered, ...remaining];
    },

    getSiteNameByCode(siteCode: string): string {
      const site = this.list.find(s => s.siteCode === siteCode);
      return site?.siteName || '';
    },

    getSiteByCode(siteCode: string): API.Site | undefined {
      return this.list.find(s => s.siteCode === siteCode);
    },

  },
  persist: true,
});
